/**
 * 「このノート」検索で、一致ノードへカーソルを移動するための規則。
 *
 * ここには「どのノードが一致するか」「次／前はどれか」だけを書き、
 * 展開・フォーカス・スクロールといった画面操作は呼び出し側が行う。
 *
 * 同じ規則が Flutter 版の `app/lib/util/outline_rules.dart` にもある。
 * 規則は Web 版と Flutter 版で同一にすること（docs/AGENTS.md）。
 * テストは `tests/search-navigation.test.mjs` と
 * `app/test/outline_rules_test.dart` の両方にある。
 */

/** 規則の判断に必要なぶんだけのツリー。並び順と本文だけを見る。 */
export interface SearchTreeView {
  /** 親の子ID列。ルート直下は parentId に null を渡す */
  childrenOf(parentId: number | null): number[];
  /** ノード本文。存在しないノードは空文字 */
  textOf(id: number): string;
}

/** ツリー全体のノードIDを、文書順（親の直後に子が続く順）で返す。 */
export function collectDocumentOrderIds(tree: SearchTreeView): number[] {
  const ids: number[] = [];
  const walk = (parentId: number | null) => {
    for (const id of tree.childrenOf(parentId)) {
      ids.push(id);
      walk(id);
    }
  };
  walk(null);
  return ids;
}

/**
 * 検索語を空白類（半角・全角スペース、タブ、改行）で語句に分け、小文字にして返す。
 * 連続空白は畳み、空の語句は除く。重複は除かない（判定結果が変わらないため）。
 *
 * 同じ規則が PHP の `search_query_terms()`（api/lib/db/search_index.php）と、
 * Flutter 版の `app/lib/util/outline_rules.dart` にもある。規則は同一にすること。
 */
export function splitSearchTerms(query: string): string[] {
  return String(query ?? '')
    .toLowerCase()
    .split(/\s+/u)
    .filter((term) => term !== '');
}

/** テキストが全ての語句を含むか（AND）。語句が空なら false。大文字小文字は区別しない。 */
export function textMatchesAllTerms(text: string, terms: string[]): boolean {
  if (terms.length === 0) {
    return false;
  }
  const lowered = String(text ?? '').toLowerCase();
  return terms.every((term) => lowered.includes(term));
}

/**
 * 検索語に一致するノードIDを文書順で返す。
 *
 * - 空白で区切った語句を全て含むノードだけが一致する（AND、語句の順序は不問）。
 * - 大文字小文字は区別しない（従来のフィルターと同じ）。
 * - 折りたたまれたノードの子孫も対象にする。フィルター表示では祖先が
 *   強制展開されるため、隠れた一致も移動対象に含めてよい。
 * - 空の検索語では何も一致しない。
 */
export function collectSearchMatchIds(tree: SearchTreeView, query: string): number[] {
  const terms = splitSearchTerms(query);
  if (terms.length === 0) {
    return [];
  }
  return collectDocumentOrderIds(tree).filter((id) => textMatchesAllTerms(tree.textOf(id), terms));
}

/**
 * 現在位置から見た「次／前」の一致ノードID。なければ null。
 *
 * - 現在ノードが一致ノードそのものなら、その次の（前の）一致へ進む。
 * - 現在ノードが一致でなくても、文書順で現在位置より後（前）の
 *   いちばん近い一致へ進む。
 * - 末尾（先頭）を過ぎたら先頭（末尾）へ回る。
 * - 現在ノードが無い・ツリーに無い場合は、先頭（末尾）の一致を返す。
 */
export function nextSearchMatchId(
  matchIds: number[],
  orderedIds: number[],
  currentId: number | null,
  direction: 1 | -1
): number | null {
  if (matchIds.length === 0) {
    return null;
  }
  const positionById = new Map(orderedIds.map((id, index) => [id, index]));
  const currentPosition = currentId !== null && positionById.has(currentId) ? positionById.get(currentId)! : null;

  if (direction > 0) {
    if (currentPosition !== null) {
      for (const id of matchIds) {
        if ((positionById.get(id) ?? -1) > currentPosition) {
          return id;
        }
      }
    }
    return matchIds[0];
  }

  if (currentPosition !== null) {
    for (let index = matchIds.length - 1; index >= 0; index--) {
      const id = matchIds[index];
      if ((positionById.get(id) ?? Number.POSITIVE_INFINITY) < currentPosition) {
        return id;
      }
    }
  }
  return matchIds[matchIds.length - 1];
}

/** 現在ノードが何件目の一致か（1始まり）。一致でなければ 0。 */
export function searchMatchPosition(matchIds: number[], currentId: number | null): number {
  if (currentId === null) {
    return 0;
  }
  const index = matchIds.indexOf(currentId);
  return index < 0 ? 0 : index + 1;
}
